import React, { useState } from 'react';
import { mintAgentSvid, downloadAgentSpiffeYaml } from '../../services/api';
import { SVID_STATUS, svidStatusLabel } from '../../data/svidStatus';
import { identityAgentPath } from '../../data/identityRoutes';
import { EYEBROW, MONO, NOTE } from './typeScale';
import Pill from './Pill';

/**
 * The identity the platform stamped, shown rather than asserted.
 *
 * <h3>Why this is a step</h3>
 *
 * Stage 02's definition is "SPIFFE/SVID issued; cryptographic name stamped".
 * Protect used to cover it with one sentence of narration and two steps that
 * belong to Register — minting a gateway key and copying a snippet. A gateway
 * key authenticates the registration; it is not the agent's name. The name,
 * the trust domain it belongs to, the certificate's state and the claims
 * carried in its extension are the evidence, and they are what the platform's
 * own Identity tab shows.
 *
 * <h3>Two things the first version of this got wrong</h3>
 *
 * It read `svidActive` alone, so a certificate that expired twenty minutes ago
 * was reported to a tenant as Active. The state now comes from the same four
 * buckets the identity panel uses, shared in data/svidStatus.js.
 *
 * And it said the certificate is "issued the first time the agent presents
 * itself" in every case — a sentence taken from a code comment rather than
 * from the capability flag. When the SPIRE Workload API is not wired nothing
 * is issued by presenting, and a tenant told that waits for something that
 * cannot happen. The platform says one of two things depending on
 * `workloadApiConnected`, and offers Rotate now only when it is; so does this.
 */
export default function IdentityAsk({
  identity, credentials = null, agentId, onAnswer, busy = false,
  onOpenIdentity = null,
}) {
  const [copied, setCopied] = useState(false);
  const [mint, setMint] = useState({ state: 'idle', error: null, serial: null });
  const [entry, setEntry] = useState({ state: 'idle', error: null });

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* A browser that refuses the clipboard is not a failure of the step —
         the name is on screen and can be selected. */
      setCopied(false);
    }
  };

  /* The same function the platform's own SPIFFE panel calls, against the
     endpoint that serves the file with its own filename. The copy of the YAML
     on the detail body says an entry EXISTS; assembling a second download out
     of it here would be a second path to one file, and the one that drifts is
     always the copy. */
  const takeEntry = async () => {
    setEntry({ state: 'working', error: null });
    const res = await downloadAgentSpiffeYaml(agentId);
    if (res && res.ok === false) {
      setEntry({ state: 'error',
        error: res.error || 'The entry could not be downloaded just now.' });
      return;
    }
    setEntry({ state: 'done', error: null });
  };

  const issue = async () => {
    setMint({ state: 'minting', error: null, serial: null });
    try {
      const res = await mintAgentSvid(agentId);
      if (res && res.ok === false) {
        setMint({ state: 'error', serial: null,
          error: res.error || 'The platform would not issue a certificate just now.' });
        return;
      }
      const d = (res && 'data' in res) ? res.data : res;
      /* The material it just issued. Kept so the panel can report the
         certificate that now exists rather than the read it opened with. */
      setMint({ state: 'done', error: null, serial: d?.svidSerial || null,
        expiresAt: d?.svidExpiresAt || null });
    } catch (e) {
      setMint({ state: 'error', serial: null,
        error: e?.message || 'The platform would not issue a certificate just now.' });
    }
  };

  /* Said out loud. A blank panel reads as "this agent has no identity", which
     is a different and much worse claim than "we could not read it". */
  if (identity?.error || !identity?.spiffeId) {
    return (
      <div style={COL}>
        <p style={NOTE}>
          {identity?.error
            || (identity && !identity.spiffeId
              ? 'No identity has been assigned to this agent yet. Registering one stamps the '
                + 'name it is recognised by; until then there is nothing to show.'
              : '')
            || 'Its identity is not readable from here yet. It is on the agent’s own page '
             + 'in the full platform.'}
        </p>
        <IdentityPageLink agentId={agentId} onOpen={onOpenIdentity} />
        <Continue busy={busy} onAnswer={onAnswer} identity={identity} />
      </div>
    );
  }

  /* One answer about the certificate, used by the pill, by the facts beside it
     and by what the step records. A mint here makes the read the panel opened
     with out of date, and reading the stale one had the pill saying "not
     provisioned" two lines above its own serial — the same panel
     contradicting itself about the same certificate. */
  const issued = mint.state === 'done';
  const status = issued ? SVID_STATUS.ACTIVE
    : (identity.status || SVID_STATUS.NOT_PROVISIONED);
  const serial = (issued ? mint.serial : null) || identity.svidSerial;
  const expiresAt = (issued ? mint.expiresAt : null) || identity.svidExpiresAt;
  /* A certificate exists to describe — serial, expiry — even once it has
     lapsed. */
  const live = status === SVID_STATUS.ACTIVE || status === SVID_STATUS.EXPIRING
    || status === SVID_STATUS.EXPIRED;
  /* But a lapsed one needs replacing as much as a missing one, and more
     urgently: the gateway refuses its calls while it sits there. Hanging the
     offer off "there is no certificate" left the agent that most needed a new
     one with no way to ask. */
  const needsCertificate = status === SVID_STATUS.NOT_PROVISIONED
    || status === SVID_STATUS.EXPIRED;
  const tone = status === SVID_STATUS.ACTIVE ? 'allow'
    : status === SVID_STATUS.EXPIRING ? 'hold'
      : status === SVID_STATUS.EXPIRED ? 'deny' : 'neutral';

  const c = identity.claims || {};
  const rows = [
    ['Kind', c.kind],
    ['Autonomy', c.autonomy],
    ['Risk tier', c.riskTier],
    ['Trust score', c.trustScore == null ? null : String(c.trustScore)],
    ['Policies', c.policyCount == null ? null : String(c.policyCount)],
    ['Frameworks', (c.frameworks || []).join(', ') || null],
    ['Platform', c.platformVersion],
    ['Issued', c.issuedAt],
  ].filter(([, v]) => v);

  return (
    <div style={COL}>
      <p style={NOTE}>
        {/* What the platform actually does with this name. It does NOT present it:
            the certificate is minted and discarded in the same expression, and
            nothing in the call path performs a handshake as the agent. Saying
            otherwise would claim key-based proof the platform is not performing. */}
        No other agent carries this name. Every line of this agent&rsquo;s record is written
        against it, together with the credential that authenticated the call.
      </p>

      <div style={PANEL}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={EYEBROW}>SPIFFE name</span>
          <button type="button" className="p7-btn p7-btn--sm" style={{ marginLeft: 'auto' }}
                  onClick={() => copy(identity.spiffeId)}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <code style={{ display: 'block', marginTop: '6px', wordBreak: 'break-all',
                       ...MONO, color: 'var(--p7-ink)' }}>
          {identity.spiffeId}
        </code>

        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap',
                      alignItems: 'center', gap: '6px 18px' }}>
          <Fact label="Trust domain" value={identity.trustDomain} mono />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={EYEBROW}>Certificate</span>
            <Pill tone={tone}>{svidStatusLabel(status)}</Pill>
          </span>
          {live && serial
            ? <Fact label="Serial" value={serial} mono /> : null}
          {live && expiresAt
            ? <Fact label={status === SVID_STATUS.EXPIRED ? 'Expired' : 'Expires'}
                    value={relative(expiresAt)} /> : null}
        </div>

        {/* No certificate, or one that has run out. Which sentence applies is
            the platform's answer, not ours. One issued here moves the panel
            out of this branch, so what happened is reported below it either
            way. */}
        {needsCertificate ? (
          <div style={{ marginTop: '10px' }}>
            {identity.canMint ? (
              <>
                <p style={NOTE}>
                  {/* What is true: it lapsed, and nothing renews it here.
                      NOT that its calls are being refused — the readiness gate
                      reads a stored identity state that an expiry does not
                      update, so claiming a refusal would be claiming an
                      enforcement the platform is not performing. */}
                  {status === SVID_STATUS.EXPIRED
                    ? 'The certificate issued against this name has expired, and nothing '
                      + 'renews it on its own. Its name still holds — issue a new one to '
                      + 'put a live certificate behind it.'
                    : 'Its name exists; no certificate has been issued against it yet.'}
                </p>
                <button type="button" className="p7-btn p7-btn--sm"
                        style={{ marginTop: '7px' }}
                        disabled={mint.state === 'minting'}
                        onClick={issue}>
                  {mint.state === 'minting' ? 'Issuing…' : 'Issue it now'}
                </button>
              </>
            ) : (
              <>
                <p style={NOTE}>
                  {status === SVID_STATUS.EXPIRED
                    ? 'Its certificate has expired, and nothing can issue a new one from here '
                    : 'No certificate has been issued, and nothing can issue one from here '}
                  — the platform’s SPIRE Workload API is not wired. Its name still holds; an
                  administrator connects the Workload API to provision certificates.
                </p>
                {/* What an administrator needs in order to do that. The entry
                    names this agent and the selectors that identify it, and it
                    is pasted into the SPIRE server they run. Offered only where
                    one exists — a control that hands over nothing is worse than
                    none. */}
                {identity.registrationEntryYaml ? (
                  <>
                    <p style={{ ...NOTE, marginTop: '7px' }}>
                      Its SPIRE registration entry is ready to hand over: it names this
                      agent and the selectors that identify it, and an administrator
                      applies it on the SPIRE server they run.
                    </p>
                    <button type="button" className="p7-btn p7-btn--sm"
                            style={{ marginTop: '7px' }}
                            disabled={entry.state === 'working'}
                            onClick={takeEntry}>
                      {entry.state === 'working' ? 'Preparing…'
                        : entry.state === 'done' ? 'Registration entry saved'
                          : 'Download registration entry'}
                    </button>
                    {entry.state === 'error' ? (
                      <p style={{ ...NOTE, marginTop: '6px',
                                  color: 'var(--p7-deny-ink, #b91c1c)' }}>
                        {entry.error}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {/* What this step did, said outside the branch above: a certificate
            issued here flips that branch off, and the confirmation went with
            it — leaving a panel that had silently changed with nothing saying
            why. */}
        <div style={{ marginTop: mint.state === 'idle' ? 0 : '10px' }}>
            {mint.state === 'done' ? (
              <p style={{ ...NOTE, color: 'var(--p7-allow-ink, #166534)' }}>
                Certificate issued{mint.serial ? ` — serial ${mint.serial}.` : '.'}
              </p>
            ) : null}
            {mint.state === 'error' ? (
              <p style={{ ...NOTE, color: 'var(--p7-deny-ink, #b91c1c)' }}>{mint.error}</p>
            ) : null}
        </div>
      </div>

      {/* What it presents on the wire, beside the name it answers to. The
          plaintext was shown once at the mint and cannot be shown again, so
          what is readable is which credentials exist, whether each is live,
          and the prefix that matches one to a line in a log. */}
      {credentials ? (
        <div style={PANEL}>
          <span style={EYEBROW}>What it authenticates with</span>
          {credentials.error ? (
            <p style={{ ...NOTE, marginTop: '7px' }}>{credentials.error}</p>
          ) : credentials.keys.length === 0 ? (
            <p style={{ ...NOTE, marginTop: '7px' }}>
              No credentials are on file for it. Its keys are issued at registration; one
              can be reissued from its page in the full platform.
            </p>
          ) : (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column',
                          gap: '7px' }}>
              {credentials.keys.map((k) => (
                <div key={k.id} style={{ display: 'flex', alignItems: 'center',
                                         flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ font: 'var(--p7-text-body)',
                                 fontFamily: 'var(--p7-font-sans)',
                                 fontWeight: 600, color: 'var(--p7-ink)' }}>
                    {k.kind}
                  </span>
                  <Pill tone={k.live ? 'allow' : 'hold'}>{k.status}</Pill>
                  {k.prefix ? (
                    <code style={MONO}>
                      {k.prefix}…
                    </code>
                  ) : null}
                  {k.invocations > 0 ? (
                    <span style={{ ...NOTE, marginLeft: 'auto' }}>
                      {k.invocations} call{k.invocations === 1 ? '' : 's'}
                      {k.lastUsedAt ? `, last ${relative(k.lastUsedAt)}` : ''}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {rows.length ? (
        <div style={PANEL}>
          <span style={EYEBROW}>Carried in the certificate</span>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap',
                        gap: '6px 18px' }}>
            {rows.map(([label, value]) => <Fact key={label} label={label} value={value} />)}
          </div>
        </div>
      ) : null}

      <IdentityPageLink agentId={agentId} onOpen={onOpenIdentity} />
      <Continue busy={busy} onAnswer={onAnswer} identity={identity} status={status} />
    </div>
  );
}

/**
 * The page this step keeps naming.
 *
 * <p>Rotation history, federation inheritance and the full SVID lifecycle live
 * on the agent's own identity page, and the step told somebody so in three
 * places without taking them there. The path comes from
 * {@code data/identityRoutes} so that this and the platform's own SPIFFE panel
 * cannot drift apart, and nothing renders when there is no agent to build one
 * for.</p>
 *
 * <p>A real href on a plain anchor, so hovering shows where it goes and
 * middle-click still opens a tab - but an ordinary click is handed to the page
 * instead of followed. Every step in this wizard is presentational and the page
 * above it owns navigation; letting the browser follow this one was a document
 * navigation that re-bootstrapped the whole application and left the walk
 * behind. Without a handler it stays a working link rather than a dead one.</p>
 */
function IdentityPageLink({ agentId, onOpen }) {
  const href = identityAgentPath(agentId);
  if (!href) return null;
  const follow = (e) => {
    /* The browser's own gestures for "somewhere else" are left alone. */
    if (!onOpen || e.defaultPrevented || e.button !== 0
        || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onOpen(href);
  };
  return (
    <p style={NOTE}>
      <a href={href} onClick={follow}
         style={{ color: 'var(--p7-accent)', fontWeight: 600,
                  textDecoration: 'none' }}>
        Open its identity page
      </a>
      {' — rotation history, federation and the full certificate lifecycle.'}
    </p>
  );
}

/** "in 3h" / "4d ago" — the part a person acts on. */
function relative(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return iso;
  const abs = Math.abs(ms);
  const n = abs < 3_600_000 ? `${Math.max(1, Math.round(abs / 60_000))}m`
    : abs < 86_400_000 ? `${Math.round(abs / 3_600_000)}h`
      : `${Math.round(abs / 86_400_000)}d`;
  return ms >= 0 ? `in ${n}` : `${n} ago`;
}

function Continue({ busy, onAnswer, identity, status = null }) {
  return (
    <div>
      <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
              onClick={() => onAnswer({
                seen: true,
                spiffeId: identity?.spiffeId || null,
                /* A refused read and an agent with no name are different
                   answers; the node above the trail has to say which. */
                unreadable: Boolean(identity?.error),
                /* What the record should say is what is true when they moved
                   on — including a certificate issued here a moment ago. The
                   panel has already worked that out; deciding it a second time
                   here is how the two came to disagree. */
                status: status || identity?.status || null,
              })}>
        Continue
      </button>
    </div>
  );
}

function Fact({ label, value, mono }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
      <span style={EYEBROW}>{label}</span>
      <span className={mono ? 'p7-mono' : undefined}
            style={{ font: 'var(--p7-text-body)',
                     fontFamily: mono ? undefined : 'var(--p7-font-sans)',
                     fontSize: mono ? '12px' : undefined, color: 'var(--p7-ink)' }}>
        {value}
      </span>
    </span>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
};
